import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';

export default async function downloadHelper(url, file) {
  try {
    const now = new Date();

    const formatted =
      now.getFullYear() +
      '-' +
      String(now.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(now.getDate()).padStart(2, '0') +
      '_' +
      String(now.getHours()).padStart(2, '0') +
      '-' +
      String(now.getMinutes()).padStart(2, '0') +
      '-' +
      String(now.getSeconds()).padStart(2, '0');

    let name = `${file}_${formatted}`;
    let filename = name;

    if (!Capacitor.isNativePlatform()) {
      try {
        const response = await fetch(getUrlWithWidth(url));

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const a = document.createElement('a');

        a.href = blobUrl;
        a.download = filename;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(blobUrl);

        return {
          message: `${name} downloaded`,
          status: 'success'
        };
      } catch (err) {
        return {
          message: err?.message || `${name} downloaded failed`,
          status: 'error'
        };
      }
    }

    const response = await fetch(getUrlWithWidth(url));
    const blob = await response.blob();
    const base64 = await blobToBase64(blob);
    const mimeType = response.headers.get('content-type') || blob.type;
    let extension = getExtensionFromMime(mimeType);

    if (!extension || mimeType === 'application/octet-stream') {
      extension = getExtensionFromUrl(url);
    }
    if (!filename.includes('.') && extension) {
      filename = `${name}${extension}`;
    }

    let directory = Directory.Documents;

    if (Capacitor.getPlatform() === 'ios') {
      directory = Directory.Data;
    }

    await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory,
    });

    // iOS: Show share sheet after saving
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const { Share } = await import('@capacitor/share');
        const fileUri = (
          await Filesystem.getUri({ path: filename, directory })
        ).uri;

        const shareResult = await Share.share({
          title: 'Image',
          text: 'Saved image',
          url: fileUri,
          dialogTitle: 'Share Image',
        });

        // Show snackbar based on share result
        let msg = `${name} downloaded successfully`;

        if (shareResult && shareResult.activityType) {
          switch (shareResult.activityType) {
            case 'com.apple.UIKit.activity.SaveToCameraRoll':
              msg = `${name} saved to Photos`;
              break;
            case 'com.apple.DocumentManagerUICore.SaveToFiles':
              msg = `${name} saved to Files`;
              break;
            case 'com.apple.UIKit.activity.CopyToPasteboard':
              msg = `${name} copied to clipboard`;
              break;
            case 'net.whatsapp.WhatsApp.ShareExtension':
              msg = `${name} shared to WhatsApp`;
              break;
            default:
              msg = `${name} shared`;
          }
        }

        return {
          message: msg,
          status: 'success'
        };
      } catch (shareErr) {
        return {
          message: `Download succeeded, ${name} saved to Documents`,
          status: 'success'
        };
      }
    } else {
      return {
        message: `${name} saved to Documents`,
        status: 'success'
      };
    }
  } catch (e) {
    return {
      message: 'Download failed',
      status: 'error'
    };
  }
}

const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      const base64data = reader.result.split(',')[1];

      resolve(base64data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const getUrlWithWidth = (url) => {
  if (!url) return '';

  return url.includes('?') ? `${url}&width=2048` : `${url}?width=2048`;
};

const getExtensionFromMime = (mimeType) => {
  if (!mimeType) return '';

  const map = {
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp',
    'image/gif': '.gif',
    'application/pdf': '.pdf',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',

    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',

    'application/vnd.ms-powerpoint': '.ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',

    'audio/mpeg': '.mp3',
    'audio/mp3': '.mp3',
    'audio/wav': '.wav',
    'audio/x-wav': '.wav',
    'audio/aac': '.aac',
    'audio/m4a': '.m4a',
    'audio/mp4': '.m4a',
    'audio/ogg': '.ogg',
    'audio/webm': '.webm',
  };

  return map[mimeType] || '';
};

const getExtensionFromUrl = (url) => {
  try {
    const cleanUrl = url.split('?')[0];
    const parts = cleanUrl.split('.');

    if (parts.length > 1) {
      return '.' + parts.pop().toLowerCase();
    }
  } catch (e) {
    return '';
  }

  return '';
};
